<template>
    <div>
        <rkm-data-table
            :fields="fields"
            :repository="repositories.agendamento"
            :tbody-tr-class="rowClass"
            class="tabela-paciente"
            ref="DataTable"
            sort-by="nome"
        >
            <template v-slot:cell(nome)="{item}">
                {{ item.nome }}

                <template v-if="item.nomeSocial">
                    <template v-if="item.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                        <strong>({{item.nomeSocial}})</strong>
                    </template>

                    <template v-else>
                        ({{item.nomeSocial}})
                    </template>
                </template>

                <span
                    v-if="item.obitoEm"
                    class="ml-1"
                    title="Paciente em óbito"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="cross" />
                </span>

                <span
                    v-if="item.bloqueado"
                    class="ml-1"
                    title="Cadastro bloqueado"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="ban" />
                </span>

                <span
                    v-if="isProvisorioOuTemporario(item)"
                    class="ml-1"
                    title="Paciente provisório/temporário"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="hourglass-half" />
                </span>

                <span
                    v-if="item.classificacaoId == enums.classificacao.OUTRO_MUNICIPIO"
                    class="ml-1"
                    title="Paciente não munícipe"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="map-pin" />
                </span>

                <span
                    v-if="!item.cpf && !item.cns"
                    class="ml-1"
                    title="Paciente sem CPF e CNS"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="exclamation-triangle" />
                </span>

                <span
                    v-if="item.pacienteResponsavelFamiliar"
                    class="ml-1"
                    title="Responsável familiar"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="crown" />
                </span>

                <span
                    v-if="isIdoso(item.dataNascimento)"
                    class="ml-1"
                    title="Idoso"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="blind" />
                </span>

                <span
                    v-if="hasTea(item)"
                    class="ml-1"
                    title="Pessoa com Transtorno do Espectro Autista - TEA"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="infinity" />
                </span>

                <span
                    v-if="hasPcd(item)"
                    class="ml-1"
                    title="Pessoa com Deficiência - PCD"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="wheelchair" />
                </span>
            </template>

            <template v-slot:cell(actions)="{item}">
                <rkm-button
                    @click="create(item)"
                    authorize="create-ambulatorio-agendamento"
                    color="primary"
                    small
                    title="Visualizar"
                >
                    <rkm-icon icon="search" />
                </rkm-button>
            </template>
        </rkm-data-table>

        <rkm-row class="mt-3">
            <rkm-col>
                <p class="m-0">
                    <span class="legend-color purple" /> Paciente em óbito<br>
                    <span class="legend-color danger" /> Cadastro bloqueado<br>
                    <span class="legend-color orange" /> Paciente provisório ou temporário<br>
                    <span class="legend-color brown" /> Paciente não munícipe<br>
                    <span class="legend-color warning" /> Paciente sem CPF e CNS
                </p>
            </rkm-col>
        </rkm-row>
    </div>
</template>

<script>
// Enums.
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Mixins.
import {repository} from '@/mixins'

// Others.
import moment from 'moment'

export default {
    created() {
        this.repositories = {
            agendamento: this.getConectaSusRepository('ambulatorio.agendamento'),
        }
    },
    data: () => ({
        enums: {
            classificacao,
            subclassificacao,
            preferenciaNome,
            deficienciaBase,
            deficiencia,
        },
        fields: [
            {key: 'nome', label: 'Nome (Nome social)', name: 'cidadao.nome', searchable: true, sortable: true},
            {key: 'nomeSocial', label: 'Nome social', name: 'cidadao.nomeSocial', searchable: true, sortable: true, visible: false},
            {key: 'dataNascimento', label: 'Data de nascimento', name: 'cidadao.dataNascimento', searchable: true, sortable: true},
            {key: 'prontuario', label: 'Prontuário municipal', name: 'paciente.prontuario', searchable: true, sortable: true},
            {key: 'cpf', label: 'CPF', name: 'cidadao.cpf', searchable: true, sortable: true},
            {key: 'cns', label: 'CNS', name: 'cidadao.cns', searchable: true, sortable: true},
            {key: 'nomeMae', label: 'Nome da mãe', name: 'cidadao.nomeMae', searchable: true, sortable: true},
            {key: 'classificacaoDescricao', label: 'Classificação', name: 'classificacao.descricao', searchable: true, sortable: true},
            {key: 'validadeFim', label: 'Validade do cadastro', name: 'validadeFim', searchable: true, sortable: true},
            {key: 'actions', label: 'Ações', name: 'actions', class: 'col-min'},
        ],
    }),
    methods: {
        isProvisorioOuTemporario(item) {

            return item.classificacaoId == this.enums.classificacao.PROVISORIO
                || item.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO
        },
        isIdoso(dataNascimento) {

            return !!dataNascimento && moment(dataNascimento, 'DD/MM/YYYY').isValid()
                && moment().diff(moment(dataNascimento, 'DD/MM/YYYY'), 'years') >= 60
        },
        getDeficienciasBaseIds(item) {

            return item.deficienciasBaseIds
                ? item.deficienciasBaseIds.split(',').map(Number)
                : []
        },
        hasTea(item) {

            return this.getDeficienciasBaseIds(item).includes(this.enums.deficienciaBase.TEA)
                || item.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        hasPcd(item) {

            return this.getDeficienciasBaseIds(item).some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!item.deficienciaId && item.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        rowClass(item, type) {
            if(item && type === 'row') {
                if (item.obitoEm) {
                    return 'table-purple'
                }
                if (item.bloqueado) {
                    return 'table-danger'
                }
                if (this.isProvisorioOuTemporario(item)) {
                    return 'table-orange'
                }
                if (item.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                    return 'table-brown'
                }
                if (!item.cpf && !item.cns) {
                    return 'table-warning'
                }
            }
        },
        create(item) {

            const id = item.id

            if (item.enderecoDesatualizadoBloqueado) {

                this.showAlert(
                    'O endereço do paciente ainda não foi atualizado pela Base Nacional. Atualize o cadastro para continuar.',
                    'Atenção'
                )

                return false
            }

            if (item.diasCadastroDesatualizado > 0) {

                const confirm = async () => {

                    if (this.isModal) {
                        this.$emit('create', id)
                    } else {
                        this.$router.push({name: 'ambulatorio-agendamento-create', params: {id}})
                    }

                    return false
                }

                let msg = `Paciente com cadastro desatualizado há <strong>${item.diasCadastroDesatualizado}</strong>
                    dia(s). Deseja continuar?`

                if (!item.isOrigemBaseNacional) {

                    msg = `Paciente com cadastro desatualizado há <strong>${item.diasCadastroDesatualizado}</strong>
                        dia(s). Endereço do usuário também deve ser atualizado. Deseja continuar?`
                }

                this.showConfirmation(msg, confirm)

                return false
            }

            if (this.isModal) {
                this.$emit('create', id)
            } else {
                this.$router.push({name: 'ambulatorio-agendamento-create', params: {id}})
            }
        },
    },
    mixins: [repository],
    props: {
        isModal: {type: Boolean}
    },
}
</script>

<style scoped>
.legend-color {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 4px;
}
.legend-color.purple {
    background-color: #6f42c1;
}
.legend-color.danger {
    background-color: var(--danger);
}
.legend-color.orange {
    background-color: #F28C28;
}
.legend-color.brown {
    background-color: #6F4E37;
}
.legend-color.warning {
    background-color: var(--warning);
}

.tabela-paciente >>> .table-orange {
    border-color: #F28C28;
    background-color: #FFE0B2;
}

.tabela-paciente >>> .table-brown {
    border-color: #6F4E37;
    background-color: #D2B48C;
}

.tabela-paciente >>> .table-purple {
    border-color: #6f42c1;
    background-color: #E1D4F2;
}
</style>